"use client";

/* ═══════════ LES 5 SITES — page générique à thème ═══════════
   Chaque domaine déroule la parade des 13 maisons « le long du site » :
   un salon successif par marque (sectionheader + grille + voir plus),
   une barre d'ancres à comptes façon catalogue royal, et des blocs
   propres au site (Mawlid, vidéo, portails, packs…). */

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Globe2, Search, X } from "lucide-react";
import {
  MARQUES, TOTAL_VISUELS, type Marque, type Produit, type Vedette,
} from "@/data/catalog";
import { EDITO, GAMMES_BOUYE, SITES, siteParSlug } from "@/data/sites";
import { ProductLightbox, Crown } from "./ui-extras";
import {
  BandeSignature, BlockCountdown, BlockMawlid, BlockPacks, BlockPortails,
  BlockVedettes, BlockVideo, BlockVitrine, CarteProduit,
  FORMATS, matchFormat, normaliser, ordinalFr, trierProduits,
  type FormatFiltre,
} from "./blocks-sites";
import type { View } from "./types";

interface Props {
  siteSlug: string;
  onNav: (v: View) => void;
  onAddProduit: (slug: string, nomMarque: string, num: number, nomProduit: string, image: string) => void;
  onAddPack: (idx: number) => void;
}

type Tri = "catalogue" | "az" | "za";
const LIMITE_PAS = 12;

interface PlanSalon {
  id: string;
  label: string;
  marque: Marque;
  produits: Produit[]; // tous les produits du salon (avant filtres)
  titre: string;
  texte: string;
  rang: number;
  rangLabel: string;
}

export function SitePage({ siteSlug, onNav, onAddProduit, onAddPack }: Props) {
  const site = siteParSlug(siteSlug);

  /* ── états ── */
  const [favoris, setFavoris] = useState<Set<string>>(new Set());
  const [limites, setLimites] = useState<Record<string, number>>({});
  const [lb, setLb] = useState<{ slug: string; num: number } | null>(null);
  const [actif, setActif] = useState<string>("");
  const [recherche, setRecherche] = useState("");
  const [format, setFormat] = useState<FormatFiltre>("tous");
  const [tri, setTri] = useState<Tri>("catalogue");

  const filtrageActif = site.filtres && (format !== "tous" || tri !== "catalogue");
  const rechercheActive = site.filtres && normaliser(recherche).length >= 2;

  /* ── plan des salons (la parade des maisons) ── */
  const plan: PlanSalon[] = useMemo(() => {
    const bouye = MARQUES.find((m) => m.slug === "bouye-ndiamsyl")!;
    const salons: PlanSalon[] = [];
    let rang = 0;

    if (site.modeGammesBouye) {
      const labels = ["Canettes 33 cl", "PET & famille", "Verre & prestige", "Coffrets de gala"];
      GAMMES_BOUYE.forEach((g, i) => {
        const produits = bouye.produits.filter((p) => g.filtre(p.num, p.name));
        if (produits.length === 0) return;
        rang += 1;
        salons.push({ id: g.id, label: labels[i], marque: bouye, produits, titre: g.titre, texte: g.sous, rang, rangLabel: "Salon " + ordinalFr(i + 1) + " de la maison verte" });
      });
    }

    site.ordreMarques.forEach((slug) => {
      const m = MARQUES.find((x) => x.slug === slug);
      if (!m) return;
      if (site.modeGammesBouye && slug === "bouye-ndiamsyl") return; // déjà déclinée en gammes
      rang += 1;
      const ed = EDITO[slug];
      salons.push({
        id: "salon-" + slug, label: m.nom, marque: m, produits: m.produits,
        titre: ed ? ed.titre : m.nom, texte: ed ? ed.texte : m.univers,
        rang, rangLabel: m.imperiale ? "Maison impériale — elle précède toutes" : ordinalFr(rang) + " maison le long du site",
      });
    });
    return salons;
  }, [site]);

  /* ── produits affichés par salon (filtres + tri) ── */
  const produitsSalon = useCallback(
    (s: PlanSalon): Produit[] => {
      let l = s.produits;
      if (filtrageActif) l = l.filter((p) => matchFormat(p.contenance, format));
      return trierProduits(l, tri);
    },
    [filtrageActif, format, tri]
  );

  /* ── résultats de recherche (site .online) ── */
  const resultats: { marque: Marque; produit: Produit }[] = useMemo(() => {
    if (!rechercheActive) return [];
    const q = normaliser(recherche);
    const out: { marque: Marque; produit: Produit }[] = [];
    MARQUES.forEach((m) =>
      m.produits.forEach((p) => {
        if (normaliser(p.name).includes(q) || normaliser(m.nom).includes(q)) out.push({ marque: m, produit: p });
      })
    );
    return out.slice(0, 60);
  }, [rechercheActive, recherche]);

  /* ── observation des salons pour la barre d'ancres ── */
  useEffect(() => {
    const ids = plan.map((s) => s.id);
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActif(visible[0].target.id);
        }
      },
      { rootMargin: "-38% 0px -55% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [plan, rechercheActive]);

  /* ── actions ── */
  const ajouter = useCallback(
    (m: Marque, p: Produit) => onAddProduit(m.slug, m.nom, p.num, p.name, p.file),
    [onAddProduit]
  );
  const basculerFavori = useCallback((cle: string) => {
    setFavoris((prev) => {
      const n = new Set(prev);
      if (n.has(cle)) n.delete(cle);
      else n.add(cle);
      return n;
    });
  }, []);
  const voirPlus = useCallback((id: string) => {
    setLimites((prev) => ({ ...prev, [id]: (prev[id] ?? LIMITE_PAS) + LIMITE_PAS }));
  }, []);
  const defilerVers = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  /* ── panneau de la marque courante pour la visionneuse ── */
  const lbMarque = lb ? MARQUES.find((m) => m.slug === lb.slug) : undefined;
  const lbIndex = lb && lbMarque ? lbMarque.produits.findIndex((p) => p.num === lb.num) : -1;

  const bloc = (b: string) => {
    switch (b) {
      case "countdown": return <BlockCountdown key={b} />;
      case "vedettes":
        return (
          <BlockVedettes
            key={b}
            vedettes={site.vedettes}
            sous={"Les créations choisies par la maison pour " + site.domaine + " — cliquez, agrandissez, offrez."}
            onAdd={(v: Vedette) => onAddProduit(v.marqueSlug, v.marque, v.num, v.nom, v.file)}
            onVoir={(v: Vedette) => setLb({ slug: v.marqueSlug, num: v.num })}
          />
        );
      case "mawlid": return <BlockMawlid key={b} onAdd={ajouter} />;
      case "vitrine": return <BlockVitrine key={b} onNavMarques={() => onNav({ nom: "marques" })} />;
      case "video": return <BlockVideo key={b} />;
      case "portails": return <BlockPortails key={b} />;
      case "packs": return <BlockPacks key={b} onAdd={onAddPack} />;
      default: return null;
    }
  };

  const nbTotal = plan.reduce((a, s) => a + s.produits.length, 0);

  return (
    <div>
      {/* ═══════════ CHANGEUR DE DOMAINES — les 5 sites en onglets ═══════════ */}
      <div className="border-b border-[#D4AF37]/20 bg-[#03150d]/85 backdrop-blur-sm" role="tablist" aria-label="Les 5 sites de l'Empire">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-2 overflow-x-auto empire-scroll">
          <span className="hidden lg:inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] shrink-0 pr-2">
            <Globe2 className="h-3.5 w-3.5" /> Les 5 sites
          </span>
          {SITES.map((s) => {
            const courant = s.slug === site.slug;
            return (
              <button
                key={s.slug}
                role="tab"
                aria-selected={courant}
                onClick={() => !courant && onNav({ nom: "site", slug: s.slug })}
                className={
                  "site-tab shrink-0 " + (courant ? "actif" : "")
                }
              >
                {s.onglet}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════ HÉROS DU SITE ═══════════ */}
      <section className={"relative overflow-hidden hero-" + site.theme}>
        <div className="absolute inset-0">
          <img src={site.heroVisuel} alt={site.heroAlt} className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#04180f]/78 via-[#04180f]/55 to-[#04180f]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#04180f]/85 via-transparent to-[#04180f]/60" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 md:py-28 text-center">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-center gap-5"
          >
            <p className="px-5 py-2 rounded-full border border-[#D4AF37]/55 bg-[#04180f]/70 backdrop-blur-sm tracking-[0.2em] text-[#F6E27A] text-xs md:text-sm font-semibold">
              {site.domaine}
            </p>
            <p className="tracking-[0.45em] text-[#D4AF37] text-xs uppercase">{site.particule}</p>
            <Crown className="h-11 w-18 drop-shadow-[0_4px_20px_rgba(212,175,55,0.5)]" />
            <h1 className="font-display text-4xl md:text-7xl font-black tracking-tight gold-text drop-shadow-[0_6px_30px_rgba(212,175,55,0.3)]">
              {site.nom}
            </h1>
            <p className="font-display italic text-xl md:text-3xl text-[#F5EFE0]/90">{site.slogan}</p>
            <div className="gold-line w-64" />
            <p className="text-sm md:text-base text-[#F5EFE0]/70 max-w-3xl leading-relaxed">{site.narration}</p>
            <p className="px-5 py-2 rounded-full border border-[#D4AF37]/40 bg-[#2a040d]/60 text-[#F6E27A] font-display italic text-sm">
              « Le Premier et le Meilleur est Adji Anta Diop »
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-3">
              <a href="#parade" className="gold-btn px-7 py-3.5 rounded-xl font-semibold tracking-wide inline-flex items-center justify-center gap-2">
                Parcourir les {plan.length} salons <ArrowRight className="h-4 w-4" />
              </a>
              <button
                onClick={() => onNav({ nom: "marques" })}
                className="px-7 py-3.5 rounded-xl font-semibold tracking-wide inline-flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#F6E27A] hover:bg-[#D4AF37]/10 transition-colors"
              >
                La vitrine des 13 marques
              </button>
            </div>
          </motion.div>
        </div>

        {/* chiffres + complétion des visuels */}
        <div className="relative border-t border-[#D4AF37]/15 bg-[#03150d]/80">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center mb-5">
              {[
                ["13", "maisons de prestige"],
                [String(TOTAL_VISUELS), "créations signées"],
                ["8", "portails partenaires"],
                ["5", "sites frères"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="font-display text-2xl md:text-3xl font-bold gold-text">{n}</p>
                  <p className="text-[11px] text-[#F5EFE0]/55 mt-0.5">{l}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] mb-2">
              <span>Complétion des visuels</span>
              <span>{TOTAL_VISUELS} / {TOTAL_VISUELS} · 100 %</span>
            </div>
            <div className="h-2 rounded-full bg-[#D4AF37]/15 overflow-hidden">
              <div className="h-full w-full bg-gradient-to-r from-[#F6E27A] via-[#D4AF37] to-[#B8860B]" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ BLOCS AVANT LA PARADE ═══════════ */}
      {site.avant.map(bloc)}

      {/* ═══════════ LA PARADE DES MAISONS — rangées le long du site ═══════════ */}
      <section id="parade" className="scroll-mt-36" aria-label="La parade des maisons">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 md:pt-20 pb-10 text-center">
          <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">Le long du site</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[#F5EFE0]">
            Les <span className="gold-text">13 Maisons</span>, salon après salon
          </h2>
          <p className="text-[#F5EFE0]/60 max-w-2xl mx-auto mt-4">
            {nbTotal} créations rangées le long de {site.domaine} — chaque maison occupe
            son salon, du premier numéro au dernier coffret. Suivez la barre d&rsquo;ancres,
            ou laissez-vous porter par la parade.
          </p>
          <div className="gold-line w-80 mx-auto mt-6" />
        </div>

        {/* ── barre d'ancres + filtres (collante) ── */}
        <div className="sticky top-16 z-40 border-y border-[#D4AF37]/20 bg-[#03150d]/95 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 space-y-2.5">
            {!rechercheActive && (
              <div className="flex gap-2 overflow-x-auto empire-scroll" role="navigation" aria-label="Aller à un salon">
                {plan.map((s) => {
                  const affiches = filtrageActif ? produitsSalon(s).length : s.produits.length;
                  return (
                    <button key={s.id} onClick={() => defilerVers(s.id)} className={"chip-nav shrink-0 " + (actif === s.id ? "actif" : "")}>
                      {s.label}
                      <span className="text-[10px] opacity-70 ml-1.5">{affiches}/{s.produits.length}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {site.filtres && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="relative flex-1 min-w-52">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#D4AF37]" />
                  <input
                    type="search"
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    placeholder="Chercher une création, une marque…"
                    aria-label="Chercher une création ou une marque"
                    className="w-full pl-10 pr-9 py-2.5 rounded-xl bg-[#04180f]/80 border border-[#D4AF37]/35 text-sm text-[#F5EFE0] placeholder:text-[#F5EFE0]/35 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
                  />
                  {recherche && (
                    <button onClick={() => setRecherche("")} aria-label="Effacer la recherche" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#F5EFE0]/50 hover:text-[#F6E27A]">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </label>
                <div className="flex gap-1.5 flex-wrap items-center" aria-label="Filtrer par format">
                  {FORMATS.map((f) => (
                    <button key={f.valeur} onClick={() => setFormat(f.valeur)} aria-pressed={format === f.valeur} className={"chip-nav shrink-0 " + (format === f.valeur ? "actif" : "")}>
                      {f.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1.5 items-center" aria-label="Trier les produits">
                  {([["catalogue", "Catalogue"], ["az", "A → Z"], ["za", "Z → A"]] as const).map(([v, l]) => (
                    <button key={v} onClick={() => setTri(v)} aria-pressed={tri === v} className={"chip-nav shrink-0 " + (tri === v ? "actif" : "")}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── résultats de recherche ── */}
        {rechercheActive ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
            <p className="text-[#F5EFE0]/70 mb-6">
              <strong className="text-[#F6E27A]">{resultats.length}</strong> création(s) trouvée(s) pour « {recherche} ».
            </p>
            {resultats.length === 0 ? (
              <p className="text-center text-[#F5EFE0]/50 italic py-16">
                Aucune création ne répond à cette quête — essayez « bouye », « coffret » ou « oud »…
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {resultats.map(({ marque, produit }) => (
                  <CarteProduit
                    key={marque.slug + "/" + produit.num}
                    marque={marque}
                    produit={produit}
                    favori={favoris.has(marque.slug + "/" + produit.num)}
                    onFavori={() => basculerFavori(marque.slug + "/" + produit.num)}
                    onVoir={() => setLb({ slug: marque.slug, num: produit.num })}
                    onAdd={() => ajouter(marque, produit)}
                    sousTitre={marque.nom + (produit.contenance ? " · " + produit.contenance : "")}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ── les salons, dans l'ordre du site ── */
          plan.map((s) => {
            const affiches = produitsSalon(s);
            const limite = limites[s.id] ?? LIMITE_PAS;
            const visibles = affiches.slice(0, limite);
            return (
              <section key={s.id} id={s.id} className="scroll-mt-40 border-t border-[#D4AF37]/12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                    <div className="max-w-3xl">
                      <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] mb-2.5">
                        <span className="px-2.5 py-0.5 rounded-full border border-[#D4AF37]/40 bg-[#04180f]/70">{s.rangLabel}</span>
                      </p>
                      <h3 className="font-display text-2xl md:text-4xl font-bold text-[#F5EFE0]">{s.titre}</h3>
                      <p className="text-sm md:text-base text-[#F5EFE0]/60 mt-3 leading-relaxed">{s.texte}</p>
                    </div>
                    <span
                      className="px-3.5 py-1.5 rounded-full text-xs font-semibold border shrink-0"
                      style={{ background: s.marque.couleur + "55", borderColor: s.marque.couleurDouce, color: s.marque.texte }}
                    >
                      {affiches.length} / {s.produits.length} créations
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {visibles.map((p) => (
                      <CarteProduit
                        key={p.num}
                        marque={s.marque}
                        produit={p}
                        favori={favoris.has(s.marque.slug + "/" + p.num)}
                        onFavori={() => basculerFavori(s.marque.slug + "/" + p.num)}
                        onVoir={() => setLb({ slug: s.marque.slug, num: p.num })}
                        onAdd={() => ajouter(s.marque, p)}
                      />
                    ))}
                  </div>

                  {affiches.length > visibles.length && (
                    <div className="text-center mt-9">
                      <button onClick={() => voirPlus(s.id)} className="btn-showmore">
                        Voir plus de créations <span className="opacity-75">(+{Math.min(LIMITE_PAS, affiches.length - visibles.length)})</span>
                      </button>
                    </div>
                  )}
                </div>
              </section>
            );
          })
        )}
      </section>

      {/* ═══════════ BLOCS APRÈS LA PARADE ═══════════ */}
      {site.apres.map(bloc)}

      {/* ═══════════ LES 5 SITES FRÈRES ═══════════ */}
      <section className="border-t border-[#D4AF37]/15 bg-[#03150d]/60" aria-label="Les 5 sites de l'Empire">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-10">
            <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3 flex items-center justify-center gap-2 justify-center">
              <Globe2 className="h-4 w-4" /> Changez de domaine
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#F5EFE0]">
              Les <span className="gold-text">5 Sites</span> de l&rsquo;Empire
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SITES.map((s) => {
              const courant = s.slug === site.slug;
              return (
                <button
                  key={s.slug}
                  onClick={() => !courant && onNav({ nom: "site", slug: s.slug })}
                  className={"card-empire rounded-2xl px-5 py-4 text-left group " + (courant ? "ring-2 ring-[#D4AF37]/60" : "")}
                  aria-current={courant ? "page" : undefined}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="font-display font-bold text-[#F5EFE0] group-hover:text-[#F6E27A] transition-colors">{s.domaine}</span>
                    {courant && <span className="text-[10px] tracking-widest uppercase text-[#F6E27A] shrink-0">vous êtes ici</span>}
                  </span>
                  <span className="block text-xs text-[#F5EFE0]/55 mt-1">{s.role}</span>
                  {!courant && <span className="mt-2 inline-flex items-center gap-1 text-[11px] tracking-widest uppercase text-[#F6E27A]">Visiter <ArrowRight className="h-3 w-3" /></span>}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <BandeSignature />

      {/* ═══════════ VISIONNEUSE ═══════════ */}
      {lbMarque && lbIndex >= 0 && (
        <ProductLightbox
          marque={lbMarque}
          index={lbIndex}
          onClose={() => setLb(null)}
          onNav={(dir) => {
            if (!lb) return;
            const n = lbMarque.produits.length;
            const i = (lbIndex + dir + n) % n;
            setLb({ slug: lb.slug, num: lbMarque.produits[i].num });
          }}
          onAdd={(p) => ajouter(lbMarque, p)}
        />
      )}
    </div>
  );
}
